/**
 * Date and seat helpers of the driver shifts page (admin#74, api#198).
 *
 * THE DATES ARE PALESTINE DATES. The API reads a shift's `date` as the
 * Palestine date on which it starts, and the page must ask for the same week.
 * A browser elsewhere (or a laptop set to UTC) would otherwise open the page
 * on the wrong day around midnight. So "today" comes from `Asia/Gaza`, and the
 * arithmetic below is on "YYYY-MM-DD" strings in UTC, which has no daylight
 * saving to move a date.
 */
import { WEEKDAYS } from "./dateFormat";

// The weekday list and its names live in utils/dateFormat.js, the one place
// for them. Re-exported here for the shift pages.
export { WEEKDAYS, WEEKDAY_LABELS, weekdayOf } from "./dateFormat";

/**
 * @param {Date} [at]
 * @returns {string} the Palestine date of that moment, "YYYY-MM-DD"
 */
export const palestineToday = (at = new Date()) =>
    new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Gaza",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(at);

/**
 * @param {string} date - "YYYY-MM-DD"
 * @param {number} days
 * @returns {string} "YYYY-MM-DD"
 */
export const addDays = (date, days) => {
    const moved = new Date(`${date}T00:00:00Z`);
    moved.setUTCDate(moved.getUTCDate() + days);
    return moved.toISOString().slice(0, 10);
};

/**
 * How full a cell is, for its label and its tone.
 *
 * @param {{seats: number, used: number}} cell
 * @returns {{label: string, tone: "empty"|"open"|"full"|"over"}}
 */
export const seatState = ({ seats, used }) => {
    const label = `${used}/${seats}`;
    if (used > seats) return { label, tone: "over" };
    if (seats > 0 && used === seats) return { label, tone: "full" };
    if (used === 0) return { label, tone: "empty" };
    return { label, tone: "open" };
};

/**
 * The seats form of a shift: one whole number for each weekday.
 *
 * @param {Record<string, number>} [seats]
 * @returns {Record<string, string>} strings, for the inputs
 */
export const seatsFormValues = (seats = {}) =>
    Object.fromEntries(WEEKDAYS.map((day) => [day, String(Number(seats[day]) || 0)]));

/**
 * Reads the seats form back. `null` when a value is not a whole number from 0
 * to 500, the API's limit.
 *
 * @param {Record<string, string>} values
 * @returns {Record<string, number>|null}
 */
export const parseSeatsForm = (values) => {
    const seats = {};
    for (const day of WEEKDAYS) {
        const raw = String(values[day] ?? "").trim();
        const number = Number(raw);
        if (raw === "" || !Number.isInteger(number) || number < 0 || number > 500) return null;
        seats[day] = number;
    }
    return seats;
};
